import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Save, Loader2 } from 'lucide-react';

const TextverwaltungTab = () => {
  const [fixedTexts, setFixedTexts] = useState({
    willkommenstext: '',
    info_text: '',
    pruefungseinleitung: '',
    pruefung_nicht_bestanden_note: '',
    pruefung_nicht_bestanden_text: '',
    pruefung_kein_ergebnis_text: '',
    offline_text: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchFixedTexts();
  }, []);

  const fetchFixedTexts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/fixed-texts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFixedTexts({
          willkommenstext: data.willkommenstext || '',
          info_text: data.info_text || '',
          pruefungseinleitung: data.pruefungseinleitung || '',
          pruefung_nicht_bestanden_note: data.pruefung_nicht_bestanden_note || 'Nicht genügend',
          pruefung_nicht_bestanden_text: data.pruefung_nicht_bestanden_text || '',
          pruefung_kein_ergebnis_text: data.pruefung_kein_ergebnis_text || 'Dein Prüfungsergebnis ist noch nicht verfügbar. Bitte schau später noch einmal vorbei!',
          offline_text: data.offline_text || 'Derzeit Offline'
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden der Texte:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Texte' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const token = localStorage.getItem('admin_token');

      const response = await fetch(`${backendUrl}/api/admin/fixed-texts`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(fixedTexts)
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Texte erfolgreich gespeichert!' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        throw new Error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setMessage({ type: 'error', text: 'Fehler beim Speichern der Texte' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFixedTexts(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-600">Verwalte alle Texte die im Teilnehmer-Dashboard angezeigt werden</p>
        <Button 
          onClick={handleSave}
          disabled={saving}
          size="sm"
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
          Speichern
        </Button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Willkommenstext */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Willkommensnachricht</CardTitle>
          <p className="text-sm text-gray-600">
            Optional: Wird unter dem Namen im Dashboard angezeigt (nur im Anmeldungsmodus)
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fixedTexts.willkommenstext}
            onChange={(e) => handleChange('willkommenstext', e.target.value)}
            placeholder="z.B. Willkommen bei unserer Musikschule..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] resize-y"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Info Text */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Info Text</CardTitle>
          <p className="text-sm text-gray-600">
            Wird über den Kursdetails angezeigt (nur im Anmeldungsmodus)
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fixedTexts.info_text}
            onChange={(e) => handleChange('info_text', e.target.value)}
            placeholder="z.B. Wichtige Informationen zum Kurs..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] resize-y"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Prüfungseinleitung */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Prüfungseinleitung (Bestanden)</CardTitle>
          <p className="text-sm text-gray-600">
            Wird nach den Prüfungsergebnissen angezeigt (nur im Prüfungsmodus wenn bestanden)
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fixedTexts.pruefungseinleitung}
            onChange={(e) => handleChange('pruefungseinleitung', e.target.value)}
            placeholder="z.B. Hier finden Sie Ihr Prüfungsergebnis..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] resize-y"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Prüfung nicht bestanden - Note Definition */}
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader>
          <CardTitle className="text-lg">Prüfung nicht bestanden - Note</CardTitle>
          <p className="text-sm text-gray-600">
            Definieren Sie, welche Note als &quot;Nicht bestanden&quot; gilt (z.B. &quot;Nicht genügend&quot;)
          </p>
        </CardHeader>
        <CardContent>
          <input
            type="text"
            value={fixedTexts.pruefung_nicht_bestanden_note}
            onChange={(e) => handleChange('pruefung_nicht_bestanden_note', e.target.value)}
            placeholder="z.B. Nicht genügend"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </CardContent>
      </Card>

      {/* Prüfung nicht bestanden - Text */}
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader>
          <CardTitle className="text-lg">Prüfung nicht bestanden - Nachricht</CardTitle>
          <p className="text-sm text-gray-600">
            Text, der angezeigt wird, wenn die Prüfung nicht bestanden wurde. Platzhalter: <code className="bg-gray-100 px-1 rounded">{'{lehrer}'}</code> <code className="bg-gray-100 px-1 rounded">{'{telefon}'}</code>
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fixedTexts.pruefung_nicht_bestanden_text}
            onChange={(e) => handleChange('pruefung_nicht_bestanden_text', e.target.value)}
            placeholder="z.B. Oje…dieses Mal hat es leider nicht geklappt. Für nähere Infos dazu kontaktiere {lehrer} {telefon}"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[120px] resize-y"
            rows={5}
          />
        </CardContent>
      </Card>

      {/* Prüfung kein Ergebnis - Text */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader>
          <CardTitle className="text-lg">Prüfung - Noch kein Ergebnis</CardTitle>
          <p className="text-sm text-gray-600">
            Text, der angezeigt wird, wenn noch kein Prüfungsergebnis vorhanden ist (Prüfungsmodus)
          </p>
        </CardHeader>
        <CardContent>
          <textarea
            value={fixedTexts.pruefung_kein_ergebnis_text}
            onChange={(e) => handleChange('pruefung_kein_ergebnis_text', e.target.value)}
            placeholder="z.B. Dein Prüfungsergebnis ist noch nicht verfügbar. Bitte schau später noch einmal vorbei!"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[100px] resize-y"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Offline Text */}
      <Card className="border-l-4 border-l-red-500">
        <CardHeader>
          <CardTitle className="text-lg">Offline-Text</CardTitle>
          <p className="text-sm text-gray-600">
            Wird angezeigt, wenn das System offline ist (über Einstellungen steuerbar)
          </p>
        </CardHeader>
        <CardContent>
          <input
            type="text"
            value={fixedTexts.offline_text}
            onChange={(e) => handleChange('offline_text', e.target.value)}
            placeholder="z.B. Derzeit Offline"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default TextverwaltungTab;
